import { Component } from 'react'
import SelectDrugTest from './OrderServices/SelectDrugTest'
import SelectAlcoholTest from './OrderServices/SelectAlcoholTest'
import SelectPhysicalExam from './OrderServices/SelectPhysicalExam'
import { ServiceDataModel } from './OrderModels'
import { TestReasonType } from '../../api/OrderRequests/OrderRequestsModel/TestReasonType'
import { TestData } from './OrderRequest'
import ServiceType, { ServiceTypeCode, GetDrugServiceType, GetAlcoholServiceType, GetPhysicalServiceType, GetOHSServiceType, GetCHSServiceType } from "../../api/Administration/Model/ServiceType"
import Service, { ServiceCode, ServicesByServiceTypeId, AccountsForServices, IsServiceCodePOCT, IsServiceCodeCHSBL } from "../../api/Administration/Model/Service"
import AccountService from "../../api/Administration/Model/AccountService"
import SelectOHS from './OrderServices/SelectOHS'
import SelectCHS from './OrderServices/SelectCHS'
import OrderDefaults from '../Administration/OrderDefaults'
import { OrderDefault } from '../../api/Administration/OrderDefaults'

interface ServicesSelectionProps {
  company?: number
  location?: number
  reason?: TestReasonType
  services: ServiceDataModel[]
  maximumDrugTests: number
  maximumAlcoholTests: number
  maximumPhysicalTests: number
  showDrugSection?: boolean
  showAlcoholSection?: boolean
  showPhysicalSection?: boolean
  showOHSSection?: boolean
  showCHSSection?: boolean
  denyAddServices: boolean
  completingPendingOrders?: boolean
  drugTestData?: TestData
  alcoholTestData?: TestData,
  orderableServiceTypes: ServiceType[],
  orderableServices: Service[],
  orderableAccountServices: AccountService[],
  defaults: OrderDefault[],
  onAddService: (data: ServiceDataModel) => void
}

const ReasonReturnToDuty = "Return To Duty"
const ReasonFollowUp = "Follow-up"

export default class ServicesSelection extends Component<ServicesSelectionProps> {
  render() {
    const { onAddService } = this.props
    const isDot = this.props.completingPendingOrders && (this.props.drugTestData?.dot || this.props.alcoholTestData?.dot)
    const hasServices = (isDOT: boolean) => {
      return this.props.services
        .filter(item => ![ServiceCode.POCT, ServiceCode.Physical, ServiceCode.PhysicalDOT, ServiceCode.Drug, ServiceCode.DrugDOT].includes(item.serviceCode))
        .filter(item => ![ServiceTypeCode.OHS].includes(item.serviceTypeCode))
        .filter(item => ![ServiceTypeCode.CHS].includes(item.serviceTypeCode))
        .filter(item => item.isDOT === isDOT)
        .length > 0
    }
    const filterServicesByType = (types: ServiceTypeCode[]) => {
      return this.props.services.filter(item => types.includes(item.serviceTypeCode))
    }
    const filterServicesByMarketplace = (marketplace: boolean) => {
      return this.props.services.filter(item => item.isMarketplace === marketplace)
    }

    // Regulated (DOT) test type is available to select if no any Non-Regulated (NON-DOT) test types in ordered services list
    const canAddDOT = this.props.completingPendingOrders && !isDot ? false : !hasServices(false)
    // Non-Regulated (NON-DOT) test type is available to select if no any Regulated (DOT) test types in ordered services list
    const canAddNonDOT = this.props.completingPendingOrders && isDot ? false : !hasServices(true)
    const canAddPOCT = !this.props.completingPendingOrders
    const canAddFLEX = !this.props.completingPendingOrders
    const canAddDrugDOT = !(this.props.completingPendingOrders && !isDot)
    const canAddDrugNonDOT = !(this.props.completingPendingOrders && isDot)

    // Whether panels should be disabled
    const canAddDrugService = true
    const canAddAlcoholService = filterServicesByType([ServiceTypeCode.Alcohol]).length < this.props.maximumAlcoholTests
    const canAddPhysicalService = filterServicesByType([ServiceTypeCode.Physical]).length < this.props.maximumPhysicalTests
    const canAddOHSService = true
    const canAddCHSService = true

    // Observed checkbox should be checked and unable to change for Reasons "Return To Duty" and "Follow-up"
    // Implemented according to Steve W. decision made on scrum 5/19/2021
    // Matching by name according to WF 1.0 logic (src\app\pages\order\order.component.ts:1949)
    const isObservedMandatory = this.props.reason && (this.props.reason.Description === ReasonReturnToDuty || this.props.reason.Description === ReasonFollowUp) || false
    const orderableServiceTypes = this.props.orderableServiceTypes;
    const orderableServices = this.props.orderableServices;
    const orderableAccountServices = this.props.orderableAccountServices;

    const drugServiceType = GetDrugServiceType(orderableServiceTypes);
    const alcoholServiceType = GetAlcoholServiceType(orderableServiceTypes);
    const physicalServiceType = GetPhysicalServiceType(orderableServiceTypes);
    const ohsServiceType = GetOHSServiceType(orderableServiceTypes)
    const chsServiceType = GetCHSServiceType(orderableServiceTypes)

    const drugServices = ServicesByServiceTypeId(orderableServices, drugServiceType.ServiceTypeID);
    const alcoholServices = ServicesByServiceTypeId(orderableServices, alcoholServiceType.ServiceTypeID);
    const physicalServices = ServicesByServiceTypeId(orderableServices, physicalServiceType.ServiceTypeID);
    const ohsServices = ServicesByServiceTypeId(orderableServices, ohsServiceType.ServiceTypeID)
    const chsServices = ServicesByServiceTypeId(orderableServices, chsServiceType.ServiceTypeID)

    const drugAccountServices = AccountsForServices(drugServices, orderableAccountServices);
    const alcoholAccountServices = AccountsForServices(alcoholServices, orderableAccountServices);
    const physicalAccountServices = AccountsForServices(physicalServices, orderableAccountServices);
    const ohsAccountServices = AccountsForServices(ohsServices, orderableAccountServices)
    const chsAccountServices = AccountsForServices(chsServices, orderableAccountServices)

    const marketplaceServicesCount = filterServicesByMarketplace(true).length;
    const notMarketplaceServicesCount = filterServicesByMarketplace(false).length;

    return (
      <div className="py-2">
        {(this.props.showDrugSection || this.props.showAlcoholSection || this.props.showPhysicalSection) && <span className="font-weight-bold">PLEASE SELECT SERVICES:</span>}
        {this.props.showDrugSection && <SelectDrugTest disabled={!canAddDrugService}
          open={this.props.completingPendingOrders}
          company={this.props.company}
          location={this.props.location}
          isObservedMandatory={isObservedMandatory}
          isDot={this.props.drugTestData?.dot}
          labAccountNumber={this.props.drugTestData?.labAccountNumber}
          panelId={this.props.drugTestData?.panelID}
          regulatoryModeID={this.props.drugTestData?.regulatoryModeID}
          employeeCategoryID={this.props.drugTestData?.employeeCategoryID}
          onAdd={onAddService}
          canAddDOT={canAddDrugDOT}
          canAddNonDOT={canAddDrugNonDOT}
          canAddPOCT={canAddPOCT}
          canAddFLEX={canAddFLEX}
          orderableServiceType={drugServiceType}
          orderableServices={drugServices}
          orderableAccountServices={drugAccountServices}
          defaults={this.props.defaults}
          marketplaceServicesCount={marketplaceServicesCount}
          notMarketplaceServicesCount={notMarketplaceServicesCount}
          denyAddService={this.props.denyAddServices}
          />}
        {this.props.showAlcoholSection && <SelectAlcoholTest disabled={!canAddAlcoholService}
          open={this.props.completingPendingOrders}
          isDot={this.props.alcoholTestData?.dot}
          selectedTestCode={this.props.alcoholTestData?.eventType}
          onAdd={onAddService}
          canAddDOT={canAddDOT}
          canAddNonDOT={canAddNonDOT}
          orderableServiceType={alcoholServiceType}
          orderableServices={alcoholServices}
          orderableAccountServices={alcoholAccountServices}
          marketplaceServicesCount={marketplaceServicesCount}
          notMarketplaceServicesCount={notMarketplaceServicesCount}
          denyAddService={this.props.denyAddServices}
          />}
        {this.props.showPhysicalSection && <SelectPhysicalExam disabled={!canAddPhysicalService}
          open={this.props.completingPendingOrders}
          onAdd={onAddService}
          canAddDOT={true}
          canAddNonDOT={true}
          orderableServiceType={physicalServiceType}
          orderableServices={physicalServices}
          orderableAccountServices={physicalAccountServices}
          marketplaceServicesCount={marketplaceServicesCount}
          notMarketplaceServicesCount={notMarketplaceServicesCount}
          denyAddService={this.props.denyAddServices}
          />}
          {this.props.showOHSSection && <SelectOHS disabled={!canAddOHSService}
            services={this.props.services}
            open={this.props.completingPendingOrders}
            canAddDOT={true}
            canAddNonDOT={true}
            company={this.props.company}
            location={this.props.location}
            onAdd={onAddService}
            orderableServiceType={ohsServiceType}
            orderableServices={ohsServices}
            orderableAccountServices={ohsAccountServices}
            marketplaceServicesCount={marketplaceServicesCount}
            notMarketplaceServicesCount={notMarketplaceServicesCount}
            denyAddService={this.props.denyAddServices}
          />}
          {this.props.showCHSSection && <SelectCHS disabled={!canAddCHSService}
            services={this.props.services}
            open={this.props.completingPendingOrders}
            company={this.props.company}
            location={this.props.location}
            canAddDOT={true}
            canAddNonDOT={true}
            onAdd={onAddService}
            orderableServiceType={chsServiceType}
            orderableServices={chsServices}
            orderableAccountServices={chsAccountServices}
            marketplaceServicesCount={marketplaceServicesCount}
            notMarketplaceServicesCount={notMarketplaceServicesCount}
            denyAddService={this.props.denyAddServices}
          />}
      </div>
    )
  }
}
